import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { spacesAPI, zonesAPI, floorsAPI, sedesAPI } from '../../services/api';
import { Grid3X3, Plus, Trash2, Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import { Toaster, toast } from 'sonner';

const SPACE_TYPES = [
  { value: 'hot_desk', label: 'Hot Desk' },
  { value: 'puesto_fijo', label: 'Puesto Fijo' },
  { value: 'sala_reuniones', label: 'Sala de Reuniones' },
  { value: 'zona_especial', label: 'Zona Especial' },
];

const PAGE_SIZE = 10;

const SpacesPage = () => {
  const [sedes, setSedes] = useState([]);
  const [selectedSede, setSelectedSede] = useState('');
  const [zones, setZones] = useState([]);
  const [spaces, setSpaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({ name: '', zone_id: '', space_type: 'hot_desk', capacity: 1 });
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(1);

  useEffect(() => {
    loadSedes();
  }, []);

  useEffect(() => {
    if (selectedSede) {
      loadData();
    }
  }, [selectedSede]);

  const loadSedes = async () => {
    try {
      const response = await sedesAPI.getAll();
      setSedes(response.data);
      if (response.data.length > 0) {
        setSelectedSede(response.data[0].id);
      }
    } catch (error) {
      console.error('Error loading sedes:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [spacesRes, floorsRes] = await Promise.all([
        spacesAPI.getBySede(selectedSede),
        floorsAPI.getAll(selectedSede)
      ]);
      setSpaces(spacesRes.data);
      // Build zones list from floors for the create dialog
      const allZones = [];
      for (const floor of floorsRes.data) {
        const zonesRes = await zonesAPI.getAll(floor.id);
        allZones.push(...zonesRes.data.map(z => ({ ...z, floor_name: floor.name })));
      }
      setZones(allZones);
      setPage(1);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await spacesAPI.create(formData);
      toast.success('Espacio creado exitosamente');
      setDialogOpen(false);
      setFormData({ name: '', zone_id: formData.zone_id, space_type: 'hot_desk', capacity: 1 });
      loadData();
    } catch (error) {
      toast.error('Error al crear espacio');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este espacio?')) return;
    try {
      await spacesAPI.delete(id);
      toast.success('Espacio eliminado');
      loadData();
    } catch (error) {
      toast.error('Error al eliminar espacio');
    }
  };

  const getSpaceTypeLabel = (type) => SPACE_TYPES.find(t => t.value === type)?.label || type;

  const getSpaceTypeColor = (type) => {
    switch (type) {
      case 'hot_desk': return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'puesto_fijo': return 'bg-green-50 text-green-700 border-green-200';
      case 'sala_reuniones': return 'bg-purple-50 text-purple-700 border-purple-200';
      case 'zona_especial': return 'bg-orange-50 text-orange-700 border-orange-200';
      default: return 'bg-slate-50 text-slate-700 border-slate-200';
    }
  };

  const totalPages = Math.max(1, Math.ceil(spaces.length / PAGE_SIZE));
  const paginated = spaces.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <AdminLayout>
      <Toaster position="top-right" />
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk' }}>
              Gestión de Espacios
            </h1>
            <p className="text-slate-500 text-sm mt-1">
              Administra los espacios físicos de trabajo
            </p>
          </div>
          <div className="flex items-center gap-2">
            {spaces.length > 0 && (
              <span className="text-sm text-slate-500" data-testid="spaces-total-count">{spaces.length} espacios</span>
            )}
            <Select value={selectedSede} onValueChange={setSelectedSede}>
              <SelectTrigger className="w-[180px] rounded-sm" data-testid="sede-selector">
                <SelectValue placeholder="Seleccionar sede" />
              </SelectTrigger>
              <SelectContent>
                {sedes.map((sede) => (
                  <SelectItem key={sede.id} value={sede.id}>{sede.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button 
              onClick={() => { setFormData({ ...formData, zone_id: zones[0]?.id || '' }); setDialogOpen(true); }} 
              className="bg-[#002FA7] hover:bg-[#001B66] rounded-sm"
              disabled={zones.length === 0}
              data-testid="add-space-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nuevo Espacio
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-[#002FA7]" />
          </div>
        ) : spaces.length === 0 ? (
          <Card className="border border-slate-200">
            <CardContent className="flex flex-col items-center justify-center h-64 text-center">
              <Grid3X3 className="w-12 h-12 text-slate-300 mb-4" />
              <p className="text-slate-500">No hay espacios registrados</p>
              <p className="text-sm text-slate-400 mt-1">Primero crea pisos y zonas</p>
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="data-table" data-testid="spaces-table">
                <thead>
                  <tr>
                    <th>Espacio</th>
                    <th>Tipo</th>
                    <th>Zona</th>
                    <th>Piso</th>
                    <th>Capacidad</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {paginated.map((space) => (
                    <tr key={space.id} data-testid={`space-row-${space.id}`}>
                      <td className="font-medium">{space.name}</td>
                      <td>
                        <span className={`inline-flex items-center px-2 py-1 rounded-sm text-xs font-medium border ${getSpaceTypeColor(space.space_type)}`}>
                          {getSpaceTypeLabel(space.space_type)}
                        </span>
                      </td>
                      <td>{space.zone_name}</td>
                      <td>{space.floor_name}</td>
                      <td>{space.capacity}</td>
                      <td>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(space.id)} className="text-slate-400 hover:text-red-500">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-between border-t border-slate-200 pt-4" data-testid="spaces-pagination">
                <p className="text-sm text-slate-500">
                  {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, spaces.length)} de {spaces.length}
                </p>
                <div className="flex items-center gap-1">
                  <Button variant="outline" size="icon" className="h-8 w-8 rounded-sm" disabled={page === 1} onClick={() => setPage(page - 1)} data-testid="spaces-prev-page">
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                    <Button
                      key={p}
                      variant={p === page ? 'default' : 'outline'}
                      size="icon"
                      className={`h-8 w-8 rounded-sm text-xs ${p === page ? 'bg-[#002FA7] hover:bg-[#001B66]' : ''}`}
                      onClick={() => setPage(p)}
                      data-testid={`spaces-page-${p}`}
                    >
                      {p}
                    </Button>
                  ))}
                  <Button variant="outline" size="icon" className="h-8 w-8 rounded-sm" disabled={page === totalPages} onClick={() => setPage(page + 1)} data-testid="spaces-next-page">
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}

        {/* Create Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Cabinet Grotesk' }}>Nuevo Espacio</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Zona</Label>
                <Select value={formData.zone_id} onValueChange={(value) => setFormData({ ...formData, zone_id: value })}>
                  <SelectTrigger className="rounded-sm" data-testid="space-zone-select">
                    <SelectValue placeholder="Seleccionar zona" />
                  </SelectTrigger>
                  <SelectContent>
                    {zones.map((z) => (
                      <SelectItem key={z.id} value={z.id}>{z.name} ({z.floor_name})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Nombre del espacio</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="rounded-sm"
                  placeholder="Ej: Escritorio A1"
                  data-testid="space-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Tipo de espacio</Label>
                <Select value={formData.space_type} onValueChange={(value) => setFormData({ ...formData, space_type: value })}>
                  <SelectTrigger className="rounded-sm" data-testid="space-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SPACE_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Capacidad</Label>
                <Input
                  type="number"
                  min="1"
                  value={formData.capacity}
                  onChange={(e) => setFormData({ ...formData, capacity: parseInt(e.target.value) || 1 })}
                  className="rounded-sm"
                  data-testid="space-capacity-input"
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-sm">Cancelar</Button>
                <Button type="submit" className="bg-[#002FA7] hover:bg-[#001B66] rounded-sm" disabled={saving} data-testid="save-space-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Guardar
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default SpacesPage;
